// packages/web/src/hooks/useServiceLoading.ts
// Service fetching and loading logic

import { useState, useEffect } from 'react';

import { api } from '../lib/api';
import { getApiEndpoint } from '../lib/api/endpoints';
import type { MarketplaceService } from '../types/pricing';

export interface ServiceLoadOptions {
  catalogInstanceId?: string;
  catalogSlug?: string;
  query?: string;
  category?: string;
  address?: string;
  limit?: number;
  offset?: number;
  mode?: 'full' | 'partner';
}

export interface ServiceLoadResult {
  items: MarketplaceService[];
  meta: {
    total: number;
    limit: number;
    offset: number;
    sort: string;
    order: string;
    hasMore: boolean;
  };
  addressPrompt?: {
    message: string;
    action: string;
    hiddenServicesCount: number;
    totalAvailableServices: number;
  } | null;
}

export interface UseServiceLoadingResult {
  services: MarketplaceService[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadServices: (options?: ServiceLoadOptions) => Promise<void>;
  loadMore: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook for loading marketplace services with pagination
 */
export function useServiceLoading(
  initialCatalogId?: string,
  initialCatalogSlug?: string,
  initialServices: MarketplaceService[] = [],
): UseServiceLoadingResult {
  const [services, setServices] = useState<MarketplaceService[]>(initialServices);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [lastOptions, setLastOptions] = useState<ServiceLoadOptions | null>(null);

  const loadServices = async (options: ServiceLoadOptions = {}) => {
    try {
      setLoading(true);
      setError(null);

      const fetchOptions: ServiceLoadOptions & { cache: string } = {
        mode: 'full',
        cache: Date.now().toString(),
        limit: 50,
        offset: 0,
        ...options,
      };

      console.log('[USE-SERVICE-LOADING] Loading services with options:', fetchOptions);

      const endpoint = fetchOptions.catalogSlug
        ? `${getApiEndpoint('catalogContext', 'base')}/${encodeURIComponent(fetchOptions.catalogSlug)}`
        : getApiEndpoint('services', 'catalog');

      const response = await api.get(endpoint, {
        params: fetchOptions,
      });

      const responseItems = Array.isArray(response?.items) ? response.items : [];

      console.log('[USE-SERVICE-LOADING] API response:', {
        itemCount: responseItems.length,
        hasMeta: !!response?.meta,
        hasMore: response?.meta?.hasMore,
        firstServiceWinningPerk: responseItems[0]?.winningPerk,
        allServicesHaveWinningPerk: responseItems.every((s) => s.winningPerk),
        servicesWithWinningPerk: responseItems.filter((s) => s.winningPerk).length,
      });

      setServices(responseItems);
      setHasMore(response?.meta?.hasMore || false);
      setNextOffset(responseItems.length);
      setLastOptions(fetchOptions);

      // Don't treat empty results as an error — this is normal when
      // viewing AI Skills filter (IT services empty) or a new catalog
      if (responseItems.length === 0) {
        setError(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load services';
      setError(errorMessage);
      console.error('[USE-SERVICE-LOADING] Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loading || !hasMore || !lastOptions) return;

    try {
      setLoading(true);
      setError(null);

      const fetchOptions = {
        ...lastOptions,
        offset: nextOffset,
        cache: Date.now().toString(),
      };

      console.log('[USE-SERVICE-LOADING] Loading more services:', fetchOptions);

      const endpoint = fetchOptions.catalogSlug
        ? `${getApiEndpoint('catalogContext', 'base')}/${encodeURIComponent(fetchOptions.catalogSlug)}`
        : getApiEndpoint('services', 'catalog');

      const response = await api.get(endpoint, {
        params: fetchOptions,
      });

      const responseItems = Array.isArray(response?.items) ? response.items : [];

      // Append new items to existing services
      setServices((prev) => [...prev, ...responseItems]);
      setHasMore(response?.meta?.hasMore || false);
      setNextOffset((prev) => prev + responseItems.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load more services';
      setError(errorMessage);
      console.error('[USE-SERVICE-LOADING] Error loading more services:', err);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setServices([]);
    setError(null);
    setHasMore(false);
    setNextOffset(0);
    setLastOptions(null);
  };

  // Auto-load initial services if catalog context is provided
  useEffect(() => {
    if (initialCatalogSlug) {
      loadServices({ catalogInstanceId: initialCatalogId, catalogSlug: initialCatalogSlug });
      return;
    }

    if (initialCatalogId) {
      loadServices({ catalogInstanceId: initialCatalogId });
    }
  }, [initialCatalogId, initialCatalogSlug]);

  return {
    services,
    loading,
    error,
    hasMore,
    loadServices,
    loadMore,
    reset,
  };
}
